import type { SessionArtifactFileSummary } from "../chat/chat-file-changes-summary.ts";
import type { GatewayBrowserClient } from "../gateway.ts";
import { resolveAgentIdForSession } from "../chat/resolve-session-agent.ts";

export type ArtifactCheckpoint = {
  ts: number;
  label: string;
  changeIds: string[];
  paths: string[];
};

export async function fetchArtifactCheckpoints(
  client: GatewayBrowserClient,
  params: { sessionKey: string; projectId?: string | null; limit?: number },
): Promise<ArtifactCheckpoint[]> {
  const sessionKey = params.sessionKey.trim();
  if (!sessionKey) {
    return [];
  }
  const agentId = resolveAgentIdForSession(sessionKey);
  const res = await client.request<{ checkpoints: ArtifactCheckpoint[] }>(
    "sessions.artifacts.checkpoints",
    {
      agentId,
      sessionKey,
      ...(params.projectId ? { projectId: params.projectId } : {}),
      ...(params.limit != null ? { limit: params.limit } : {}),
    },
  );
  return res?.checkpoints ?? [];
}

export async function revertArtifactChange(
  client: GatewayBrowserClient,
  params: { sessionKey: string; changeId: string; projectId?: string | null },
): Promise<boolean> {
  const sessionKey = params.sessionKey.trim();
  const changeId = params.changeId.trim();
  if (!sessionKey || !changeId) {
    return false;
  }
  const agentId = resolveAgentIdForSession(sessionKey);
  try {
    await client.request("sessions.artifacts.revert", {
      agentId,
      sessionKey,
      changeId,
      ...(params.projectId ? { projectId: params.projectId } : {}),
    });
    return true;
  } catch {
    return false;
  }
}

export async function revertArtifactsSince(
  client: GatewayBrowserClient,
  params: { sessionKey: string; sinceTs: number; projectId?: string | null },
): Promise<{ reverted: number; failed: string[] }> {
  const sessionKey = params.sessionKey.trim();
  if (!sessionKey) {
    return { reverted: 0, failed: [] };
  }
  const agentId = resolveAgentIdForSession(sessionKey);
  try {
    const res = await client.request<{ reverted: number; failed: string[] }>(
      "sessions.artifacts.revert",
      {
        agentId,
        sessionKey,
        sinceTs: params.sinceTs,
        ...(params.projectId ? { projectId: params.projectId } : {}),
      },
    );
    return { reverted: res?.reverted ?? 0, failed: res?.failed ?? [] };
  } catch {
    return { reverted: 0, failed: ["revert failed"] };
  }
}

/** Revert each file row from a composer/summary snapshot (correct subagent session per row). */
export async function revertSnapshotFiles(
  client: GatewayBrowserClient,
  files: SessionArtifactFileSummary[],
  params: { mainSessionKey: string; projectId?: string | null },
): Promise<{ reverted: number; failed: string[] }> {
  const failed: string[] = [];
  let reverted = 0;
  const mainSessionKey = params.mainSessionKey.trim();
  for (const file of [...files].reverse()) {
    const changeId = file.changeId?.trim();
    if (!changeId) {
      failed.push(`${file.path}: missing change id`);
      continue;
    }
    const sessionKey = file.sessionKey?.trim() || mainSessionKey;
    const ok = await revertArtifactChange(client, {
      sessionKey,
      changeId,
      projectId: params.projectId,
    });
    if (ok) {
      reverted += 1;
    } else {
      failed.push(file.path);
    }
  }
  return { reverted, failed };
}
